import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Bot, LogOut, History, Loader2 } from "lucide-react";
import axios from "axios";
import * as chatbotApi from "../../api/chatbot";
import { formatApiErrorDetail } from "../../utils/format";
import { brandStyle, focusRingStyle } from "../../utils/branding";
import ChatWindow from "./ChatWindow";
import Sidebar from "./Sidebar";

export default function Chatbot() {
  const { slug, conversationId: urlConvId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [userInfo, setUserInfo] = useState(() => {
    try {
      const saved = localStorage.getItem(`chatbot_user_${slug}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Login state
  const [username, setUsername] = useState(() => searchParams.get("u") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  /** Phase RAG reçue via STATUS:… (affiche un libellé dans le skeleton de chargement). */
  const [streamPhase, setStreamPhase] = useState(null);
  const [conversationId, setConversationIdState] = useState(
    urlConvId ? parseInt(urlConvId) : null,
  );
  const [conversations, setConversations] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const pendingStreamTextRef = useRef("");
  const flushTimerRef = useRef(null);
  const localMessageSeqRef = useRef(0);

  const api = axios.create({ withCredentials: true });
  const nextLocalMessageId = () =>
    `local_${Date.now()}_${localMessageSeqRef.current++}`;

  const flushAssistantChunksNow = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const toAppend = pendingStreamTextRef.current;
    pendingStreamTextRef.current = "";
    if (!toAppend) return;
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const i = next.length - 1;
      const last = next[i];
      if (last?.role !== "assistant") return prev;
      next[i] = { ...last, content: (last.content || "") + toAppend };
      return next;
    });
  };

  const enqueueAssistantChunk = (chunk) => {
    if (!chunk) return;
    pendingStreamTextRef.current += chunk;
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushAssistantChunksNow();
    }, 40);
  };

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  const setConversationId = (id) => {
    setConversationIdState(id);
    if (id) {
      navigate(`/c/${slug}/${id}`, { replace: true });
    } else {
      navigate(`/c/${slug}`, { replace: true });
    }
  };

  // Sync conversationId from URL
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (urlConvId) {
      const id = parseInt(urlConvId);
      if (!isNaN(id) && id !== conversationId) {
        setConversationIdState(id);
      }
    } else if (conversationId !== null) {
      setConversationIdState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConvId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Load config and check session
  useEffect(() => {
    if (!slug) return;

    chatbotApi
      .fetchConfig(slug)
      .then((res) => {
        const cfg = res.data;
        setConfig(cfg);
        document.documentElement.style.setProperty(
          "--brand-primary",
          cfg.primary_color,
        );
        document.documentElement.style.setProperty(
          "--brand-secondary",
          cfg.secondary_color,
        );
        document.title = `${cfg.name} — Assistant IA`;

        // Check session if we have userInfo locally
        if (userInfo) {
          chatbotApi
            .fetchMe(api, slug)
            .then((me) => {
              setUserInfo(me.data);
              setIsAuthenticated(true);
            })
            .catch(() => {
              setIsAuthenticated(false);
              setUserInfo(null);
              localStorage.removeItem(`chatbot_user_${slug}`);
            });
        }
      })
      .catch(() => setError("Chatbot introuvable."))
      .finally(() => setIsLoadingConfig(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Load conversations and messages when authenticated or conversationId changes
  const loadConversations = async () => {
    try {
      const res = await chatbotApi.fetchConversations(api, slug);
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error(err);
    }
  };

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isAuthenticated || !slug) {
      if (config?.welcome_message && !conversationId) {
        setMessages([{ role: "assistant", content: config.welcome_message }]);
      }
      return;
    }

    // Important: ne pas écraser les messages locaux pendant un stream en cours
    // (ex: quand CONV_ID arrive et déclenche un changement d'URL/conversationId).
    if (loading) return;

    loadConversations();
    if (conversationId) {
      chatbotApi
        .fetchConversation(api, slug, conversationId)
        .then((res) => setMessages(res.data.messages || []))
        .catch(() => setConversationId(null));
    } else {
      setMessages(
        config?.welcome_message
          ? [{ role: "assistant", content: config.welcome_message }]
          : [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, slug, conversationId, config, loading]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await chatbotApi.chatbotLogin(slug, username, password);
      const safe = {
        status: res.data.status,
        username: res.data.username,
        subclient: res.data.subclient,
      };
      localStorage.setItem(`chatbot_user_${slug}`, JSON.stringify(safe));
      setUserInfo(safe);
      setIsAuthenticated(true);
      chatbotApi
        .fetchMe(api, slug)
        .then((m) => setUserInfo(m.data))
        .catch(() => {});
    } catch (err) {
      setLoginError(formatApiErrorDetail(err, "Identifiants incorrects"));
    }
  };

  const handleSsoLogin = async () => {
    setLoginError("");
    try {
      const res = await axios.get("/api/sso/initiate", {
        params: {
          slug,
          auth_type: "chatbot",
          redirect_to: window.location.href,
        },
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      setLoginError(formatApiErrorDetail(err, "SSO non disponible"));
    }
  };

  const handleLogout = () => {
    chatbotApi.chatbotLogout(api, slug).finally(() => {
      setIsAuthenticated(false);
      setUserInfo(null);
      localStorage.removeItem(`chatbot_user_${slug}`);
      setConversationId(null);
    });
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const q = input.trim();
    if (!q || loading) return;

    setLoading(true);
    setStreamPhase(null);
    pendingStreamTextRef.current = "";
    setMessages((prev) => [
      ...prev,
      { localId: nextLocalMessageId(), role: "user", content: q },
      {
        localId: nextLocalMessageId(),
        role: "assistant",
        content: "",
        sources: [],
        isStreaming: true,
      },
    ]);
    setInput("");

    let responseBuffer = "";

    try {
      const resp = await chatbotApi.streamMessage(slug, q, conversationId);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        if (!chunk) continue;
        responseBuffer += chunk;

        if (responseBuffer.includes("\n")) {
          const lines = responseBuffer.split("\n");
          responseBuffer = lines.pop() || "";

          for (const line of lines) {
            const tl = line.trim();
            if (tl.startsWith("SOURCE:")) {
              try {
                const s = JSON.parse(tl.replace("SOURCE:", "").trim());
                setMessages((prev) => {
                  if (prev.length === 0) return prev;
                  const next = [...prev];
                  next[next.length - 1] = {
                    ...next[next.length - 1],
                    sources: s,
                  };
                  return next;
                });
                // On garde le skeleton actif tant qu'aucun token texte n'est reçu.
                setLoading(true);
              } catch (e) {
                console.error(e);
              }
            } else if (tl.startsWith("CONV_ID:")) {
              const id = parseInt(tl.replace("CONV_ID:", "").trim());
              if (!isNaN(id)) setConversationId(id);
            } else if (tl.startsWith("STATUS:")) {
              const ph = tl.replace(/^STATUS:\s*/i, "").trim();
              setStreamPhase(ph || null);
              // Garantit l'affichage des phases tant qu'il n'y a pas de texte.
              setLoading(true);
            } else if (line) {
              enqueueAssistantChunk(line.replace(/\r/g, "") + "\n");
              setStreamPhase(null);
            }
          }
        }

        if (
          responseBuffer &&
          !responseBuffer.startsWith("SOURCE:") &&
          !responseBuffer.startsWith("CONV_ID:") &&
          !responseBuffer.startsWith("STATUS:")
        ) {
          enqueueAssistantChunk(responseBuffer.replace(/\r/g, ""));
          setStreamPhase(null);
          responseBuffer = "";
        }
      }
      flushAssistantChunksNow();
      if (
        responseBuffer.trim() &&
        !responseBuffer.startsWith("SOURCE:") &&
        !responseBuffer.startsWith("CONV_ID:") &&
        !responseBuffer.startsWith("STATUS:")
      ) {
        enqueueAssistantChunk(responseBuffer.replace(/\r/g, ""));
      }
      flushAssistantChunksNow();
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = { ...last, isStreaming: false };
        }
        return next;
      });
    } catch (err) {
      if (err?.status === 401) {
        handleLogout();
        return;
      }
      const errText = err?.message || "Désolé, une erreur est survenue.";
      // Flush pending chunks avant d'ajouter l'erreur
      flushAssistantChunksNow();
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = { ...last, isStreaming: false };
          if (!(last.content || "").trim()) {
            next[next.length - 1].content = errText;
            next[next.length - 1].isError = true;
          } else if (!last.isError) {
            // Contenu déjà.streamé mais erreur backend → garder le contenu, marquer erreur
            next[next.length - 1] = { ...last, isStreaming: false };
          }
          return next;
        }
        return [
          ...next,
          {
            localId: nextLocalMessageId(),
            role: "assistant",
            content: errText,
            isError: true,
          },
        ];
      });
    } finally {
      // Assure que tout est flushé AVANT de désactiver loading
      flushAssistantChunksNow();
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && last.isStreaming) {
          next[next.length - 1] = { ...last, isStreaming: false };
        }
        return next;
      });
      setLoading(false);
      setStreamPhase(null);
      loadConversations();
      if (isAuthenticated && slug) {
        chatbotApi
          .fetchMe(api, slug)
          .then((m) => setUserInfo(m.data))
          .catch(() => {});
      }
    }
  };

  if (isLoadingConfig)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  if (error)
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4 text-center">
        <Bot size={64} className="text-red-500 mb-4" />
        <h1 className="text-2xl font-semibold text-stone-800 mb-2">Oups !</h1>
        <p className="text-stone-600 max-w-md">{error}</p>
      </div>
    );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-stone-200/50 p-8 border border-stone-100 animate-in fade-in zoom-in duration-500">
          <div className="flex flex-col items-center text-center mb-8">
            {config.logo_url ? (
              <div className="size-24 rounded-3xl overflow-hidden flex items-center justify-center mb-4">
                <img
                  src={config.logo_url}
                  alt={`Logo ${config.name}`}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div
                className="size-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                style={brandStyle(config)}
              >
                <Bot size={32} />
              </div>
            )}
            <h1 className="text-2xl font-semibold text-stone-900 mb-1">
              {config.name}
            </h1>
            <p className="text-stone-500">
              Connectez-vous pour commencer la conversation
            </p>
            <div className="mt-3 size-9 rounded-md overflow-hidden opacity-75">
              <img
                src="/raguia.webp"
                alt="RAG IA"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="chatbot-login-username" className="block text-sm font-semibold text-stone-700 mb-1.5 ml-1">
                Identifiant
              </label>
              <input
                id="chatbot-login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all bg-stone-50/50"
                style={focusRingStyle(config)}
                placeholder="Votre nom"
              />
            </div>
            <div>
              <label htmlFor="chatbot-login-password" className="block text-sm font-semibold text-stone-700 mb-1.5 ml-1">
                Mot de passe
              </label>
              <input
                id="chatbot-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all bg-stone-50/50"
                style={focusRingStyle(config)}
                placeholder="••••••••"
              />
            </div>
            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-medium border border-red-100 animate-in shake duration-300">
                {loginError}
              </div>
            )}
            <button
              type="submit"
              className="w-full py-3.5 rounded-xl font-bold shadow-lg hover:brightness-95 active:scale-[0.98] transition-all mt-2"
              style={brandStyle(config)}
            >
              Se connecter
            </button>
            {config?.has_sso && (
              <>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400">ou</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSsoLogin}
                  className="w-full py-3 rounded-xl font-semibold border-2 transition-all hover:brightness-95"
                  style={{
                    borderColor: config.primary_color || "#8d282a",
                    color: config.primary_color || "#8d282a",
                  }}
                >
                  Connexion SSO Entreprise
                </button>
              </>
            )}
          </form>
        </div>
        <a
          href="/confidentialite-plateforme"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-xs text-stone-500 hover:text-stone-700 underline"
        >
          Politique de confidentialite - Plateforme Raguia
        </a>
        <a
          href="https://status.valentin-fiess.fr/status/raguia"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-stone-500 hover:text-stone-700 underline"
        >
          Statut du service
        </a>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        config={config}
        userInfo={userInfo}
        conversations={conversations}
        currentConvId={conversationId}
        onSelectConv={setConversationId}
        onDeleteConv={async (id) => {
          try {
            await chatbotApi.deleteConversation(api, slug, id);
            loadConversations();
            if (id === conversationId) setConversationId(null);
          } catch (e) {
            console.error(e);
          }
        }}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-4 sm:px-6 flex items-center justify-between border-b border-stone-100 bg-white/80 backdrop-blur-md sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-stone-50 rounded-lg transition-colors text-stone-500 lg:hidden"
            >
              <History size={20} />
            </button>
            <div className="flex items-center gap-2">
              {config.logo_url ? (
                <div className="size-12 rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src={config.logo_url}
                    alt={`Logo ${config.name}`}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className="size-8 rounded-lg flex items-center justify-center text-xs font-bold"
                  style={brandStyle(config)}
                >
                  <Bot size={16} />
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-bold text-stone-900 leading-tight">
                  {config.name}
                </span>
                <div className="flex items-center gap-1">
                  <span className="size-1.5 rounded-full bg-emerald-500"></span>
                  <span className="text-[10px] text-stone-500 font-medium">
                    Assistant IA Actif
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex size-8 rounded-md overflow-hidden opacity-75 hover:opacity-100 transition-opacity mr-1">
              <img
                src="/raguia.webp"
                alt="RAG IA"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden sm:flex flex-col items-end mr-2">
              <span className="text-xs font-bold text-stone-800">
                {userInfo?.username}
              </span>
              <span className="text-[10px] text-stone-500 uppercase tracking-wider">
                {userInfo?.subclient || "Utilisateur"}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
              title="Déconnexion"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        <ChatWindow
          config={config}
          messages={messages}
          input={input}
          setInput={setInput}
          loading={loading}
          streamPhase={streamPhase}
          onSend={handleSend}
          quotas={userInfo?.quotas || {}}
        />
        <footer className="px-4 sm:px-6 py-2 border-t border-stone-100 bg-white flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href="/confidentialite-plateforme"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-stone-500 hover:text-stone-700 underline"
            >
              Politique de confidentialite - Plateforme Raguia
            </a>
            <span className="text-stone-300 text-xs">·</span>
            <a
              href="https://status.valentin-fiess.fr/status/raguia"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-stone-500 hover:text-stone-700 underline"
            >
              Statut du service
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
